import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Icon from '../components/Icon.jsx';
import TiltCard from '../components/TiltCard.jsx';
import { subjects, STAGES, minPrice45 } from '../mock';

const FILTERS = [{ id: 'all', label: 'Alle Stufen' }, ...STAGES];

function SubjectCard({ s, activeStage }) {
  const showLK = s.lk && (activeStage === 'all' || activeStage === 'ober');
  return (
    <TiltCard className="h-full">
      <div className="card-soft p-6 h-full flex flex-col" data-testid={`subject-card-${s.name}`}>
        <div className="flex items-start justify-between gap-3">
          <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white shadow-md shrink-0`}>
            <Icon name={s.icon} className="h-7 w-7" />
          </div>
          {showLK && (
            <span
              data-testid={`lk-badge-${s.name}`}
              title="Leistungskurs-Nachhilfe in der Oberstufe verfügbar"
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm"
              style={{ background: '#1a3a5c' }}
            >
              <GraduationCap className="h-3.5 w-3.5" /> LK
            </span>
          )}
        </div>
        <h3 className="mt-4 font-bold text-lg leading-tight" style={{ color: '#1a3a5c' }}>{s.name}</h3>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {STAGES.filter((st) => s.stages.includes(st.id)).map((st) => {
            const active = activeStage === st.id;
            return (
              <span
                key={st.id}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${active ? 'text-white border-transparent' : 'text-slate-600 bg-slate-50 border-slate-200'}`}
                style={active ? { background: '#3d94b8' } : {}}
              >
                {st.label}
              </span>
            );
          })}
        </div>
        {showLK && <p className="mt-2 text-xs font-medium" style={{ color: '#2d7d9a' }}>Auch als Leistungskurs-Nachhilfe</p>}
        <div className="mt-auto pt-4 flex items-center justify-between border-t border-slate-100 mt-5">
          <span className="text-xs text-slate-500">ab <strong style={{ color: '#1a3a5c' }}>{minPrice45(s)} €</strong> / 45 Min</span>
          <Link to="/buchen" className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" style={{ color: '#2d7d9a' }}>Buchen <ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
      </div>
    </TiltCard>
  );
}

export default function Subjects() {
  const [q, setQ] = useState('');
  const [stage, setStage] = useState('all');
  const resultsRef = useRef(null);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    const el = resultsRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    if (top > window.innerHeight * 0.6 || top < 0) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [stage]);

  const filtered = useMemo(() => subjects.filter((s) => {
    const matchQ = s.name.toLowerCase().includes(q.trim().toLowerCase());
    const matchS = stage === 'all' || s.stages.includes(stage);
    return matchQ && matchS;
  }), [q, stage]);

  return (
    <div>
      <section className="hero-bg py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>Fächer</span>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight" style={{ color: '#1a3a5c' }}>Nachhilfe von Grundschule bis Uni</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">{subjects.length} Fächer, vier Stufen – wähle deine Stufe und finde dein Fach.</p>

          <div className="mt-8 max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="subject-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Fach suchen, z.B. Mathematik..."
              className="pl-11 h-12 rounded-full bg-white border-slate-200 shadow-sm"
            />
          </div>

          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`stage-filter-${f.id}`}
                onClick={() => setStage(f.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  stage === f.id ? 'text-white shadow-md' : 'bg-white text-slate-700 border border-slate-200 hover:border-teal-300'
                }`}
                style={stage === f.id ? { background: '#3d94b8' } : {}}
              >
                {f.label}
              </button>
            ))}
          </div>

          {stage === 'ober' && (
            <p data-testid="lk-hint" className="mt-4 text-sm text-slate-600 inline-flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: '#1a3a5c' }}><GraduationCap className="h-3 w-3" /> LK</span>
              = auch als Leistungskurs-Nachhilfe verfügbar
            </p>
          )}
        </div>
      </section>

      <section ref={resultsRef} className="py-14 md:py-20 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p data-testid="subject-count" className="text-sm text-slate-500 mb-6">{filtered.length} {filtered.length === 1 ? 'Fach' : 'Fächer'}{stage !== 'all' ? ` · ${STAGES.find((s) => s.id === stage).label}` : ''}</p>
          {filtered.length === 0 ? (
            <div data-testid="subjects-empty" className="text-center py-20 text-slate-500">Keine Fächer gefunden. Versuche eine andere Suche oder Stufe.</div>
          ) : (
            <div key={`${stage}-${q}`} className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filtered.map((s, i) => (
                <div key={s.name} className="fade-up h-full" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
                  <SubjectCard s={s} activeStage={stage} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold" style={{ color: '#1a3a5c' }}>Dein Fach nicht dabei?</h3>
          <p className="mt-3 text-slate-600">Wir erweitern unser Angebot laufend. Schreib uns – wir finden eine Lösung.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/kontakt"><Button className="btn-teal rounded-full h-12 px-6">Kontakt aufnehmen</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
